import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Collapse } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import NavBar from "../Components/NavBar/NavBar";
import Footer from "../Components/Footer/Footer";
import axios from "axios";
import styled, { keyframes } from 'styled-components';
import { FaUserFriends } from "react-icons/fa";

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); } 
  40% { transform: scale(1); }
`;

const DotLoader = styled.div`
  display: flex; justify-content: center; align-items: center;
  height: 60px; margin: 1.5rem 0;
  div {
    width: 12px; height: 12px; margin: 0 5px;
    background-color: #4a6cf7; border-radius: 50%;
    display: inline-block; animation: ${bounce} 1.4s infinite ease-in-out both;
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
`;

const LoadingContainer = styled.div`
  display: flex; flex-direction: column; justify-content: center;
  align-items: center; min-height: 80vh; text-align: center;
`;

const LoadingText = styled.h4`
  font-weight: 600; margin-bottom: 1rem; color: #2d3748;
`;

const SubText = styled.p`
  color: #718096; margin-top: 1rem;
`;

const clientID = "BOAZZ6254G01AZZ";

const generateNonce = () => {
    const timestamp = Date.now().toString();
    const randomPart = Math.floor(Math.random() * 1e7)
        .toString()
        .padStart(7, '0');
    return (timestamp + randomPart).slice(0, 20);
};

const generateTransactionRefNo = () => {
    const now = new Date();
    const dateTime = now
        .toLocaleString('sv-SE', { timeZone: 'Asia/Singapore' })
        .replace(/\D/g, '');
    const seq = Math.floor(Math.random() * 1e6)
        .toString()
        .padStart(6, '0');
    return clientID + dateTime + seq;
};

const generateDDARefNo = () => {
    const randomNum = Math.floor(Math.random() * 1e6)
        .toString()
        .padStart(6, '0');
    return `AZZ${randomNum}01`;
};

const formatOptionLabel = ({ label, status }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
            style={{
                width: 10, height: 10, borderRadius: '50%',
                backgroundColor: status === 'Online' ? '#28a745' : '#dc3545',
                marginRight: 10,
            }}
        />
        {label}
        <span
            style={{
                marginLeft: 'auto',
                color: status === 'Online' ? '#28a745' : '#dc3545',
                fontSize: '0.8em',
            }}
        >
            {status}
        </span>
    </div>
);

const ProfilePage = () => {
    const [isCheckingEDDA, setIsCheckingEDDA] = useState(false);
    const [eddaStatus, setEddaStatus] = useState(null);
    const [open, setOpen] = useState(false);
    const [productData, setProductData] = useState(null);
    const [isBankLoading, setIsBankLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [progressWidth, setProgressWidth] = useState(0);
    const [userData, setUserData] = useState(null);
    const [giroStatus, setGiroStatus] = useState('not_setup');
    const [selectedBank, setSelectedBank] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [banks, setBanks] = useState([]);
    const [redirectStep, setRedirectStep] = useState(null);
    const [pendingTransaction, setPendingTransaction] = useState(() => {
        const saved = localStorage.getItem("pendingGiroTransaction");
        return saved ? JSON.parse(saved) : null;
    });
    const navigate = useNavigate();
    const hasFetchedRef = useRef(false);
    const isFetchingRef02 = useRef(false);

    const initializeUserData = (apiUser) => {
        const storedUser = JSON.parse(localStorage.getItem('userData')) || {};
        return {
            ...apiUser,
            ...storedUser,
            boDDARefNo:
                apiUser.boDDARefNo || storedUser.boDDARefNo || generateDDARefNo(),
            boTransactionRefNo:
                apiUser.boTransactionRefNo ||
                storedUser.boTransactionRefNo ||
                generateTransactionRefNo(),
            nonce: generateNonce(),
            timestamp: Date.now(),
            requestID: uuidv4(),
            email_address:
                storedUser.email_address || apiUser?.email_address,
        };
    };

    // Check EDDA status
    const checkEDDAStatus = async (transactionRef, bankCode) => {
        try {
            const requestID = uuidv4();

            const response = await axios.get(
                'https://www.azzenterprises.com/api/v1/edda/status',
                {
                    params: {
                        boTransactionRefNo: transactionRef,
                        applicantBankCode: bankCode,
                        clientID: clientID,
                        requestID: requestID,
                    },
                    withCredentials: true
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error checking EDDA status:', error);
            throw error;
        }
    };

    const handleCheckEDDAStatus = useCallback(async (transactionRef, bankCode) => {
        setIsCheckingEDDA(true);
        try {
            const result = await checkEDDAStatus(transactionRef, bankCode);
            setEddaStatus(result.status);

            setUserData(prev => {
                const updated = { ...prev, eddaStatus: result.status };
                localStorage.setItem("userData", JSON.stringify(updated));
                return updated;
            });
        } catch (err) {
            console.error("❌ Failed to check EDDA:", err);
        } finally {
            setIsCheckingEDDA(false);
        }
    }, []);

    // Utility: Add 1 month to given date string
    const calculateNextPaymentDate = (fromDate) => {
        if (!fromDate) return null;
        const date = new Date(fromDate);
        date.setMonth(date.getMonth() + 1);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    // New function to fetch date range from your backend API
    const fetchDateRange = useCallback(async (transactionRef) => {
        try {
            const response = await axios.get(
                `https://www.azzenterprises.com/api/status/update/${transactionRef}`,
                { withCredentials: true }
            );

            return response.data; // { fromDate, toDate }
        } catch (error) {
            console.error("Error fetching date range:", error);
            return null;
        }
    }, []);

    const handleBankProcessComplete = useCallback(async () => {
        if (!pendingTransaction) return;

        setRedirectStep("checking_status");

        console.log("Verifying GIRO status for transaction:", pendingTransaction);

        try {
            const isCancellation = pendingTransaction.requestType === "Cancellation";
            // const statusData = await checkEDDAStatus(
            //     pendingTransaction.boTransactionRefNo,
            //     pendingTransaction.applicantBankCode
            // );

            // const statusInfo = statusData.status || "Pending";
            const storedUser = JSON.parse(localStorage.getItem('userData'))

            let updatedUser = { ...storedUser };
            let dateRange = null;

            if (isCancellation) {
                updatedUser = {
                    ...updatedUser,
                    boName: "",
                    applicantBankCode: "",
                };
            } else {
                dateRange = await fetchDateRange(pendingTransaction.boTransactionRefNo);
                const nextPaymentDate = calculateNextPaymentDate(dateRange?.fromDate);

                updatedUser = {
                    ...updatedUser,
                    fromDate: dateRange?.fromDate,
                    toDate: dateRange?.toDate,
                    nextPaymentDate,
                    boName: pendingTransaction.boName,
                    applicantBankCode: pendingTransaction.applicantBankCode,
                };
            }

            // Persist locally
            localStorage.setItem("userData", JSON.stringify(updatedUser));
            setUserData(updatedUser);

            // Update server
            await axios.put(
                `https://www.azzenterprises.com/api/customers/${userData.customer_id}`,
                {
                    fromDate: dateRange?.fromDate,
                    toDate: dateRange?.toDate,
                    requestType: isCancellation ? "Cancellation" : pendingTransaction.requestType,
                },
                { withCredentials: true }
            );

            // Clear session storage
            localStorage.removeItem("pendingGiroTransaction");
            localStorage.removeItem("lastBankRedirectTime");

            setTimeout(() => {
                setGiroStatus("not_setup");
            }, 2500);

        } catch (error) {
            console.error("Error checking EDDA status:", error);
            toast.error("Failed to verify GIRO status. Please try again later.");
            setRedirectStep(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingTransaction]);

    useEffect(() => {
        if (!pendingTransaction) return;
        const lastRedirectTime = localStorage.getItem("lastBankRedirectTime");
        if (lastRedirectTime && Date.now() - lastRedirectTime < 10 * 60 * 1000) {
            handleBankProcessComplete();
        }
    }, [pendingTransaction, handleBankProcessComplete]);

    useEffect(() => {
        const fetchAndSetupUser = async () => {
            if (!localStorage.getItem("userData")) {
                return navigate("/sign-in");
            }
            if (hasFetchedRef.current) return;
            hasFetchedRef.current = true;
            const storedUser = JSON.parse(localStorage.getItem('userData')) || {};
            if (!storedUser?.email_address) return navigate("/sign-in");

            try {
                const { data: apiUser } = await axios.get(
                    `https://www.azzenterprises.com/api/customers/getcustomer`,
                    { withCredentials: true }
                );

                if (!apiUser) throw new Error("No user data available");

                const initializedUser = initializeUserData(apiUser);

                let newGiroStatus = "not_setup";
                if (apiUser.requestType === "Creation") {
                    newGiroStatus = "active";
                } else if (!apiUser.requestType) {
                    newGiroStatus = "not_setup";
                }

                if (apiUser.product_name) {
                    try {
                        const { data: productInfo } = await axios.get(
                            `https://www.azzenterprises.com/api/products/info/${apiUser.product_name}`,
                            { withCredentials: true }
                        );
                        setProductData(productInfo || {});
                    } catch (err) {
                        console.warn("⚠️ Failed to fetch product info:", err.message);
                    }
                }

                const updatedUser = { ...initializedUser, giroStatus: newGiroStatus };

                localStorage.setItem("userData", JSON.stringify(updatedUser));
                setUserData(updatedUser);
                setGiroStatus(newGiroStatus);
            } catch (err) {
                console.error("Error:", err.response?.data || err.message);
                toast.error("Failed to load data. Please try again.");
            } finally {
                setTimeout(() => {
                    setIsLoading(false);
                }, 1500);
            }
        };

        fetchAndSetupUser();
    }, [navigate]);

    useEffect(() => {
        if (!isLoading) return;
        const interval = setInterval(() => {
            setProgressWidth((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 1;
            });
        }, 20);
        return () => clearInterval(interval);
    }, [isLoading]);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("userData"));
        const pending = JSON.parse(localStorage.getItem("pendingGiroTransaction"));
        const lastRedirectTime = localStorage.getItem("lastBankRedirectTime");

        if (storedUser) {
            setUserData(storedUser);
            setGiroStatus(storedUser.giroStatus || "not_setup");

            if (storedUser.giroStatus === "active") {
                fetchDateRange(storedUser.boTransactionRefNo);
            }
        }

        if (pending) {
            setPendingTransaction(pending);

            if (
                lastRedirectTime &&
                Date.now() - lastRedirectTime < 10 * 60 * 1000
            ) {
                // Run async process ONCE
                (async () => {
                    await handleBankProcessComplete();
                })();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // <-- empty dependency array, only run on mount

    useEffect(() => {
        const updateUserWithDateRange = async () => {
            if (isFetchingRef02.current) return;
            isFetchingRef02.current = true;

            try {
                const storedUser = JSON.parse(localStorage.getItem("userData"));

                if (
                    storedUser?.giroStatus === "active" &&
                    storedUser?.boTransactionRefNo &&
                    storedUser?.applicantBankCode
                ) {
                    await handleCheckEDDAStatus(
                        storedUser.boTransactionRefNo,
                        storedUser.applicantBankCode
                    );

                    const dateRange = await fetchDateRange(storedUser.boTransactionRefNo);

                    if (dateRange) {
                        const updatedUser = {
                            ...storedUser,
                            fromDate: dateRange.fromDate,
                            toDate: dateRange.toDate,
                        };

                        localStorage.setItem("userData", JSON.stringify(updatedUser));

                        setUserData(updatedUser);
                    }
                }
            } catch (err) {
                console.error("❌ Failed to update user with date range:", err);
            } finally {
                isFetchingRef02.current = false;
            }
        };

        updateUserWithDateRange();
    }, [userData?.boTransactionRefNo]);

    const handleBankSelection = (option) => {
        if (option?.status === 'Offline') {
            toast.error(
                'The selected bank is currently offline. Please choose another bank or try again later.'
            );
        }
        setSelectedBank(option);
    };

    const handleGiroSubmit = async (e) => {
        e.preventDefault();
        if (!selectedBank) {
            toast.error('Please select a bank');
            return;
        }
        if (selectedBank.status === 'Offline') {
            toast.error(
                'The selected bank is currently offline. Please choose another bank or try again later.'
            );
            return;
        }
        setIsSubmitting(true);
        try {
            const newRequestID = uuidv4();
            const newNonce = generateNonce();
            const newTimestamp = Date.now();

            const updateData = {
                applicantBankCode: selectedBank.value,
                boDDARefNo: userData.boDDARefNo,
                boName: selectedBank.label,
                clientID,
                purpose: 'COLL',
                requestID: newRequestID,
                requestType: 'Creation',
                segment: 'Retail',
                nonce: newNonce,
                timestamp: newTimestamp,
                boTransactionRefNo: userData.boTransactionRefNo,
                status: 'Pending',
                cancelFlag: 0
            };

            const response = await axios.put(
                `https://www.azzenterprises.com/api/customers/${userData.customer_id}`,
                updateData,
                { withCredentials: true }
            );

            if (response.status !== 200) {
                throw new Error(
                    `Customer update failed with status ${response.status}`
                );
            }

            const updatedUserData = {
                ...userData,
                ...updateData
            };

            localStorage.setItem('userData', JSON.stringify(updatedUserData));
            setUserData(updatedUserData);

            // Store pending transaction info
            const pendingTransaction = {
                boTransactionRefNo: userData.boTransactionRefNo,
                applicantBankCode: selectedBank.value,
                boName: selectedBank.label,
                timestamp: newTimestamp
            };

            localStorage.setItem('pendingGiroTransaction', JSON.stringify(pendingTransaction));
            localStorage.setItem('lastBankRedirectTime', Date.now().toString());

            const bankApiResponse = await axios.post(
                `https://www.azzenterprises.com/api/v1/user/creation`,
                {
                    applicantBankCode: selectedBank.value,
                    boDDARefNo: userData.boDDARefNo,
                    boName: 'AZZ ENTERPRISES PTE LTD',
                    boTransactionRefNo: userData.boTransactionRefNo,
                    clientID,
                    purpose: 'COLL',
                    requestID: newRequestID,
                    requestType: 'Creation',
                    segment: 'Retail',
                    nonce: newNonce,
                    timestamp: newTimestamp,
                },
                { withCredentials: true }
            );

            if (bankApiResponse.data.success) {
                // Redirect to bank's URL
                window.location.href = bankApiResponse.data.redirectUrl;
            }

        } catch (error) {
            console.error('GIRO setup error:', error);
            toast.error(
                error.response?.data?.message ||
                'Failed to submit GIRO request'
            );
            setIsSubmitting(false);
        }
    };

    const handleCancelGiro = async () => {
        setIsCancelling(true);
        try {
            const newNonce = generateNonce();
            const newTimestamp = Date.now();
            const newRequestID = uuidv4();

            const storedUser = JSON.parse(localStorage.getItem("userData"));

            const updateData = {
                requestType: 'Cancellation',
                nonce: newNonce,
                timestamp: newTimestamp,
                requestID: newRequestID,
                giroStatus: 'Cancellation Pending',
                boName: storedUser.boName,
                applicantBankCode: storedUser.applicantBankCode,
            };

            const updatedUser = {
                ...storedUser,
                ...updateData,
                giroStatus: 'Cancellation Pending',
            };

            localStorage.setItem('userData', JSON.stringify(updatedUser));
            setUserData(updatedUser);

            // Store pending transaction info for cancellation
            const pendingTransaction = {
                boTransactionRefNo: storedUser.boTransactionRefNo,
                applicantBankCode: storedUser.applicantBankCode,
                boName: storedUser.boName,
                timestamp: newTimestamp,
                requestType: 'Cancellation'
            };

            localStorage.setItem('pendingGiroTransaction', JSON.stringify(pendingTransaction));
            localStorage.setItem('lastBankRedirectTime', Date.now().toString());

            // Update database
            const response = await axios.put(
                `https://www.azzenterprises.com/api/customers/${userData.customer_id}`,
                updatedUser,
                { withCredentials: true }
            );

            if (response.status !== 200) {
                throw new Error(`Customer update failed with status ${response.status}`);
            }

            // Call cancellation API endpoint
            const bankApiResponse = await axios.post(
                `https://www.azzenterprises.com/bo-redirect/cancellation`,
                {
                    applicantBankCode: userData.applicantBankCode,
                    boDDARefNo: userData.boDDARefNo,
                    boName: 'AZZ ENTERPRISES PTE LTD',
                    boTransactionRefNo: userData.boTransactionRefNo,
                    clientID,
                    purpose: 'COLL',
                    requestID: newRequestID,
                    requestType: 'Cancellation',
                    segment: 'Retail',
                    nonce: newNonce,
                    timestamp: newTimestamp,
                },
                { withCredentials: true }
            );

            if (bankApiResponse.data.success) {
                // Redirect to bank's URL for cancellation
                window.location.href = bankApiResponse.data.redirectUrl;
            } else {
                throw new Error('Cancellation API call failed');
            }
        } catch (error) {
            console.error('Cancellation error:', error);
            toast.error(
                error.response?.data?.message ||
                'Failed to process cancellation'
            );
            setIsCancelling(false);


            const originalUser = {
                ...userData,
                giroStatus: 'active'
            };
            localStorage.setItem('userData', JSON.stringify(originalUser));
            setUserData(originalUser);
        }
    };

    const handleStartGiroSetup = async () => {
        setIsBankLoading(true);
        try {
            const { data: bankData } = await axios.get(
                'https://www.azzenterprises.com/api/v1/user/list',
                {
                    params: {
                        requestID: userData.requestID,
                        nonce: userData.nonce,
                        timestamp: userData.timestamp,
                    },
                    withCredentials: true,
                }
            );
            const retailBanksRaw = bankData.banks
                .filter((bank) => bank.segment === 'Retail')
                .map((bank) => ({
                    value: bank.bankCode,
                    label: bank.bankName,
                    status: bank.status,
                }));
            const uniqueRetailBanks = Array.from(
                new Map(retailBanksRaw.map((b) => [b.value, b])).values()
            );
            setBanks([...uniqueRetailBanks]);
            const updatedUser = {
                ...userData,
                requestID: uuidv4(),
                nonce: generateNonce(),
                timestamp: Date.now(),
            };
            localStorage.setItem(
                'userData',
                JSON.stringify(updatedUser)
            );
            setUserData(updatedUser);
            setGiroStatus('setup_form');
            setSelectedBank(null);
        } catch (error) {
            toast.error('Failed to load bank list. Please try again.');
            console.error('Bank list error:', error);
            setGiroStatus('not_setup');
        } finally {
            setIsBankLoading(false);
        }
    };

    if (redirectStep === 'checking_status') {
        return (
            <>
                <NavBar />
                <LoadingContainer>
                    <DotLoader>
                        <div />
                        <div />
                        <div />
                    </DotLoader>
                    <LoadingText>Checking GIRO Status</LoadingText>
                    <SubText>Please wait while we verify your GIRO arrangement...</SubText>
                </LoadingContainer>
                <Footer />
            </>
        );
    }

    if (isLoading) {
        return (
            <>
                <NavBar />
                <LoadingContainer>
                    <DotLoader>
                        <div />
                        <div />
                        <div />
                    </DotLoader>
                    <LoadingText>Loading your profile</LoadingText>
                    <div className="w-25 mb-3">
                        <div
                            className="progress"
                            style={{ height: '6px', borderRadius: '3px' }}
                        >
                            <div
                                className="progress-bar bg-primary"
                                style={{
                                    width: `${progressWidth}%`,
                                    transition: 'width 0.2s ease',
                                }}
                            />
                        </div>
                    </div>
                    <SubText>Almost there...</SubText>
                </LoadingContainer>
                <Footer />
            </>
        );
    }

    if (!userData) {
        return (
            <>
                <NavBar />
                <div className="text-center mt-5">Loading...</div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavBar />
            <Container className="py-5" style={{ minHeight: '70vh' }}>
                <Row>
                    <Col lg={12} className="mb-4">
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold" style={{ fontSize: '20px' }}>
                                    Purchased Product
                                    {userData.guarantor_for && (
                                        <span className="badge bg-primary ms-2 align-middle">
                                            <FaUserFriends className="me-1" size={14} />
                                            Guarantor
                                        </span>
                                    )}
                                </h5>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center gap-3">

                                        <div>
                                            {userData?.requestType === "Creation" && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center gap-2"
                                                    onClick={() =>
                                                        handleCheckEDDAStatus(
                                                            userData.boTransactionRefNo,
                                                            userData.applicantBankCode
                                                        )
                                                    }
                                                    disabled={isCheckingEDDA}
                                                >
                                                    {isCheckingEDDA ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                                className="me-2"
                                                            />
                                                            Checking...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-sync-alt me-2"></i>
                                                            Check Status
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Collapse Toggle */}
                                    <Button
                                        variant="link"
                                        className="text-decoration-none fw-bold"
                                        onClick={() => setOpen(!open)}
                                        aria-controls="product-info-collapse"
                                        aria-expanded={open}
                                    >
                                        {open ? (
                                            <i className="fas fa-chevron-up"></i>
                                        ) : (
                                            <i className="fas fa-chevron-down"></i>
                                        )}
                                    </Button>
                                </div>
                            </Card.Header>

                            {/* Collapsible Body */}
                            <Collapse in={open}>
                                <div id="product-info-collapse">
                                    <Card.Body>
                                        <Row className="align-items-center mb-4">
                                            <Col md={3} className="text-center">
                                                <img
                                                    src={productData?.product_img}
                                                    alt={productData?.product_name}
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "150px" }}
                                                />
                                            </Col>
                                            <Col md={9}>
                                                <h5 className="fw-bold mb-2">{productData?.product_name}</h5>
                                                <p className="text-muted mb-2">{productData?.product_description}</p>
                                                <div className="d-flex gap-3 align-items-center">
                                                    <span className="fw-bold fs-5 text-success">
                                                        ${productData?.sales_price || productData?.price}
                                                    </span>
                                                    {productData?.sales_price && (
                                                        <span className="text-muted text-decoration-line-through">
                                                            ${productData?.price}
                                                        </span>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Product Details */}
                                        <Row className="g-4">
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">Category</div>
                                                <div className="fw-semibold">{productData?.category}</div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">Color</div>
                                                <div className="fw-semibold">{productData?.color}</div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">Capacity</div>
                                                <div className="fw-semibold">{productData?.capacity}</div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">SKU</div>
                                                <div className="fw-semibold">{productData?.sku}</div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">Size & Weight</div>
                                                <div className="fw-semibold">{productData?.size_and_weight}</div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="text-muted small mb-1">Display</div>
                                                <div className="fw-semibold">{productData?.display}</div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </div>
                            </Collapse>
                        </Card>
                    </Col>

                    <Col lg={6} className="mb-4">
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <h5 className="mb-0 fw-semibold">Personal Information</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">Full Name</div>
                                        <div className="fw-semibold">{userData.full_name}</div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">Email</div>
                                        <div className="fw-semibold small">
                                            {userData.email_address}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">
                                            Contact Number
                                        </div>
                                        <div className="fw-semibold">
                                            {userData.phone_number || 'Not provided'}
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">
                                            Date of Birth
                                        </div>
                                        {userData.date_of_birth ? (
                                            <div className="fw-semibold">
                                                {new Date(userData.date_of_birth).toLocaleDateString("en-GB", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                            </div>
                                        ) : (
                                            <div className="fw-semibold">N/A</div>
                                        )}
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">ID Type</div>
                                        <div className="fw-semibold">{userData.id_type}</div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">ID Number</div>
                                        <div className="fw-semibold">{userData.id_number}</div>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">Bill Number</div>
                                        <div className="fw-semibold">{userData.bill_number}</div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">EMI Plan</div>
                                        <div className="fw-semibold">
                                            {userData.emi_plan_duration || 'N/A'} months
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col xs={6}>
                                        <div className="text-muted small mb-1">
                                            DDA Reference Number
                                        </div>
                                        <div className="fw-semibold">{userData.boDDARefNo}</div>
                                    </Col>
                                    {userData.guarantor_for && userData.guarantor_for.trim() !== "" && (
                                        <Col xs={6}>
                                            <div className="text-muted small mb-1">
                                                Guarantor For
                                            </div>
                                            <div className="fw-semibold">{userData.guarantor_for}</div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={6}>
                        <Card className="shadow-sm">
                            <Card.Body className="d-flex flex-column" style={{ minHeight: '300px' }}>
                                {giroStatus === 'not_setup' && (
                                    <>
                                        <p className="text-warning mb-2">
                                            <i className="fas fa-exclamation-circle me-2"></i>
                                            You haven't set up Giro yet.
                                        </p>
                                        <p className="text-muted mb-4">
                                            Enjoy hassle-free payment with Giro. Sign up for recurring giro via Azz with your user ID to proceed.
                                        </p>
                                        <Button variant="primary" className="mt-auto py-2" onClick={handleStartGiroSetup}>
                                            Proceed with Giro Setup
                                        </Button>
                                    </>
                                )}

                                {giroStatus === 'setup_form' && (
                                    <Form onSubmit={handleGiroSubmit}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Select Your Bank</Form.Label>
                                            <Select
                                                options={banks}
                                                value={selectedBank}
                                                onChange={handleBankSelection}
                                                placeholder={isBankLoading ? "Loading banks..." : "Search or select bank..."}
                                                isSearchable
                                                required
                                                className="basic-multi-select"
                                                classNamePrefix="select"
                                                formatOptionLabel={formatOptionLabel}
                                                getOptionValue={option => option.value}
                                                isLoading={isBankLoading}
                                                loadingMessage={() => "Loading banks..."}
                                                noOptionsMessage={() => "No banks available"}
                                                styles={{
                                                    menu: (provided) => ({
                                                        ...provided, maxHeight: 150, overflowY: 'auto',
                                                    }),
                                                    menuList: (provided) => ({
                                                        ...provided, maxHeight: 150, overflowY: 'auto',
                                                    }),
                                                }}
                                            />
                                        </Form.Group>

                                        <div className="mb-4 p-3 bg-light rounded">
                                            <p className="small mb-1 text-muted">
                                                Your DDA Reference Number:
                                            </p>
                                            <p className="fw-bold">{userData.boDDARefNo}</p>
                                            <p className="small text-muted">
                                                This number has been automatically generated for your account.
                                            </p>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="success"
                                                type="submit"
                                                className="flex-grow-1 py-2"
                                                disabled={isSubmitting || !selectedBank || isBankLoading}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    'Submit Giro Request'
                                                )}
                                            </Button>

                                            <Button variant="outline-secondary" className="py-2" onClick={() => setGiroStatus('not_setup')} disabled={isSubmitting}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </Form>
                                )}

                                {giroStatus === 'active' && (
                                    <>
                                        <div className="mb-4">
                                            <div className="d-flex align-items-center text-success mb-3">
                                                <i className="fas fa-check-circle me-2 fs-4" />
                                                <h5 className="mb-0">
                                                    {eddaStatus || 'Loading...'}
                                                </h5>
                                            </div>

                                            {userData.fromDate && userData.toDate && (
                                                <div className="alert alert-info p-3 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-calendar-check me-3 fs-4" />
                                                        <div>
                                                            <p className="mb-0">
                                                                GIRO arrangement valid from{' '}
                                                                <span className="fw-bold">{userData.fromDate}</span> to{' '}
                                                                <span className="fw-bold">{userData.toDate}</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="bg-light p-3 rounded mb-4">
                                                <p className="small text-muted mb-2">GIRO Details</p>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Bank:</span>
                                                    <span className="fw-semibold">{userData.boName}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">DDA Reference:</span>
                                                    <span className="fw-semibold">{userData.boDDARefNo}</span>
                                                </div>

                                            </div>
                                        </div>

                                        {userData.cancelFlag === 1 ? (
                                            <>
                                                <Button
                                                    variant="outline-danger"
                                                    className="mt-auto py-2 d-flex align-items-center justify-content-center"
                                                    onClick={handleCancelGiro}
                                                    disabled={isCancelling}
                                                >
                                                    {isCancelling ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                                className="me-2"
                                                            />
                                                            Processing Cancellation...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-ban me-2" />
                                                            Cancel GIRO Arrangement
                                                        </>
                                                    )}
                                                </Button>

                                                {userData.nextPaymentDate && (
                                                    <p className="small text-muted mt-2">
                                                        Please make your payment before{" "}
                                                        <span className="text-primary">{userData.nextPaymentDate}</span>{" "}
                                                        to avoid any issues.
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <div className="alert alert-danger d-flex align-items-center gap-2 p-3 rounded mt-3">
                                                <i className="fas fa-exclamation-circle fs-5"></i>
                                                <p className="mb-0 small">
                                                    RBAC permission required. Please contact the administrator to enable GIRO cancellation.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container >
            <Footer />
        </>
    );
};

export default ProfilePage;